"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.PatientNotesList = void 0;
var _react = _interopRequireDefault(require("react"));
var _momentTimezone = _interopRequireDefault(require("moment-timezone"));
var _lodash = require("lodash");
var _types = require("../types");
var _components = require("../components");
var _utils = require("../utils");
var _AuthProvider = require("./AuthProvider");
var _SessionNoteStatusBadge = require("./SessionNoteStatusBadge");
function _interopRequireDefault(obj) { return obj && obj.__esModule ? obj : { default: obj }; }
var NA_STRING = 'N/A';
var noteStatusFilters = [{
  value: _types.SessionNoteStatus.Draft,
  text: 'Not submitted'
}, {
  value: _types.SessionNoteStatus.Submitted,
  text: 'Submitted'
}];

// Legacy notes may not be associated with a booking
// which means no startsAt value
var appointmentDateSorter = function appointmentDateSorter(a, b) {
  return (0, _utils.timeSorter)('startsAt')(a, b) || (0, _utils.timeSorter)('createdAt')(a, b);
};
var statusSorter = function statusSorter(primaryPath, secondaryPath, billingCode) {
  return function (a, b) {
    if (a.isLegacyNote !== b.isLegacyNote) {
      // Legacy notes should be at the bottom
      return a.isLegacyNote ? 1 : -1;
    }
    if (billingCode) {
      if ((0, _utils.needsBillingCode)(a) !== (0, _utils.needsBillingCode)(b)) {
        return (0, _utils.needsBillingCode)(a) ? 1 : -1;
      }
    }
    var sortByPrimaryPath = (0, _utils.preferredOrderSorter)(primaryPath, [_types.SessionNoteStatus.Draft, _types.SessionNoteStatus.Submitted])(a, b);
    if (sortByPrimaryPath !== 0) {
      return sortByPrimaryPath;
    }
    var sortBySecondaryPath = (0, _utils.preferredOrderSorter)(secondaryPath, [_types.SessionNoteStatus.Draft, _types.SessionNoteStatus.Submitted])(a, b);
    if (sortBySecondaryPath !== 0) {
      return sortBySecondaryPath;
    }
    return appointmentDateSorter(a, b);
  };
};
var getNoteOwner = function getNoteOwner(note) {
  return (0, _utils.isLegacyNote)(note) ? note.creator : note.clinician;
};
var getNotesListConfig = function getNotesListConfig(_ref) {
  var onAction = _ref.onAction,
    accountId = _ref.accountId,
    showOwner = _ref.showOwner,
    possibleOwners = _ref.possibleOwners;
  return (0, _lodash.compact)([{
    title: 'Appointment date',
    dataIndex: 'startsAt',
    key: 'date',
    render: function render(text) {
      return text ? (0, _momentTimezone.default)(text).format('MM/DD/YYYY h:mma z') : 'No associated session';
    },
    sorter: appointmentDateSorter
  }, showOwner && {
    title: 'Owner',
    dataIndex: 'creator',
    render: function render(text, row) {
      var creator = getNoteOwner(row);
      return /*#__PURE__*/_react.default.createElement("div", {
        className: "text-left"
      }, /*#__PURE__*/_react.default.createElement("p", null, (0, _utils.personName)(creator)), /*#__PURE__*/_react.default.createElement("p", null, "(", (0, _utils.getRoleNameLower)(creator.role), ")"));
    },
    sorter: (0, _utils.stringSorter)(function (row) {
      return (0, _utils.personName)((0, _utils.isLegacyNote)(row) ? row.creator : row.clinician);
    }),
    filters: possibleOwners.map(function (owner) {
      return {
        value: owner.id,
        text: (0, _utils.personName)(owner)
      };
    }),
    onFilter: function onFilter(ownerId, row) {
      return (0, _utils.isLegacyNote)(row) ? row.creator.id === ownerId : row.clinician.id === ownerId;
    }
  }, {
    title: 'Patient',
    dataIndex: 'patient',
    render: function render(text, row) {
      return /*#__PURE__*/_react.default.createElement(_components.TextButton, {
        size: "small",
        underline: false,
        route: "/patients/".concat(row.patient.nickName)
      }, (0, _utils.personName)(row.patient));
    },
    sorter: (0, _utils.stringSorter)(function (row) {
      return (0, _utils.personName)(row.patient);
    })
  }, {
    title: 'Note status',
    dataIndex: 'soap.status',
    render: function render(text, row) {
      return (0, _utils.isLegacyNote)(row) ? NA_STRING : /*#__PURE__*/_react.default.createElement(_SessionNoteStatusBadge.SessionNoteStatusBadge, {
        status: text
      });
    },
    sorter: statusSorter('soap.status', 'billingCode.status'),
    defaultSortOrder: 'ascend',
    filters: noteStatusFilters,
    onFilter: function onFilter(value, row) {
      var _row$soap;
      return ((_row$soap = row.soap) === null || _row$soap === void 0 ? void 0 : _row$soap.status) === value;
    }
  }, {
    title: 'Billing status',
    dataIndex: 'billingCode.status',
    render: function render(text, row) {
      return (0, _utils.isLegacyNote)(row) ? NA_STRING : (0, _utils.needsBillingCode)(row) ? /*#__PURE__*/_react.default.createElement(_SessionNoteStatusBadge.SessionNoteStatusBadge, {
        status: text
      }) : /*#__PURE__*/_react.default.createElement(_components.Tooltip, {
        overlayClassName: "max-w-[250px]",
        title: "Billing codes are not needed for patient-no shows or unbillable appointments.",
        placement: "top"
      }, /*#__PURE__*/_react.default.createElement("div", null, /*#__PURE__*/_react.default.createElement(_components.MonumentTag, {
        type: "white"
      }, "N/A")));
    },
    sorter: statusSorter('billingCode.status', 'soap.status', true),
    filters: noteStatusFilters,
    onFilter: function onFilter(value, row) {
      var _row$billingCode;
      return ((_row$billingCode = row.billingCode) === null || _row$billingCode === void 0 ? void 0 : _row$billingCode.status) === value;
    }
  }, {
    title: 'Action',
    key: 'action',
    render: function render(text, row) {
      var _row$soap2, _row$billingCode2;
      var props;
      if ((0, _utils.isLegacyNote)(row)) {
        props = row.creator.owner ? {
          text: 'Edit',
          icon: 'edit-pencil'
        } : {
          text: 'View'
        };
      } else if (row.clinicianId !== accountId) {
        props = {
          text: 'View'
        };
      } else if (((_row$soap2 = row.soap) === null || _row$soap2 === void 0 ? void 0 : _row$soap2.status) === _types.SessionNoteStatus.Submitted && (((_row$billingCode2 = row.billingCode) === null || _row$billingCode2 === void 0 ? void 0 : _row$billingCode2.status) === _types.SessionNoteStatus.Submitted || !(0, _utils.needsBillingCode)(row))) {
        props = {
          text: 'Add addendum',
          icon: 'add-regular-24'
        };
      } else {
        props = {
          text: 'Finish',
          icon: 'edit-pencil'
        };
      }
      return /*#__PURE__*/_react.default.createElement(_components.TextButton, {
        size: "large",
        icon: props.icon,
        iconPosition: "left",
        underline: false,
        onClick: function onClick() {
          return onAction(row);
        }
      }, props.text);
    }
  }]);
};
var PatientNotesList = function PatientNotesList(_ref2) {
  var _ref2$notes = _ref2.notes,
    notes = _ref2$notes === void 0 ? [] : _ref2$notes,
    showOwner = _ref2.showOwner,
    onAction = _ref2.onAction;
  var _useAuth = (0, _AuthProvider.useAuth)(),
    user = _useAuth.user;
  var possibleOwners = (0, _lodash.uniqBy)(notes.map(getNoteOwner), 'id');
  return /*#__PURE__*/_react.default.createElement(_components.DataTable, {
    dataSource: notes,
    columns: getNotesListConfig({
      onAction: onAction,
      accountId: user.accountId,
      showOwner: showOwner,
      possibleOwners: possibleOwners
    })
  });
};
exports.PatientNotesList = PatientNotesList;